/**
 * **状态语汇 —— 所有可交互控件唯一的 hover / 按下 / 禁用出处。**
 *
 * `docs/ui-design.md` §2 那张表就是这个文件。
 *
 * ## 为什么必须只有一个出处
 *
 * 之前每个控件自己挂 `pointerover` / `pointerout`，于是同一个界面里出现了
 * **三种** hover 行为：实心按钮把酸色撤掉、空心按钮把酸色填上、
 * ghost 按钮只是字亮一点。读起来就是"这里按下去会消失，那里按下去会出现"。
 * 而这三个都"看起来合理"，所以谁也不会在评审时发现 —— 只有玩的人会觉得别扭。
 *
 * ## 状态表（第二版：改了 hover 的手感）
 *
 * | 状态 | 动作 | 方向 |
 * | --- | --- | --- |
 * | `idle` | 静止 | — |
 * | `hover` | **抬起** + 墨往 hover 那头补 | 位移**向上**、墨**只增不减** |
 * | `press` | **压下**（回到纸面再往下 1px） | 位移向下 |
 * | `disabled` | **撤墨 + 注销杠** | 墨只减 |
 *
 * ⚠️ **第一版的 hover 是"加印一版，套印错位"**（纸上的说法很对），
 * 评审的反馈是「手感太烂了，应当改为向上移动」。屏幕上的东西被鼠标掠过时，
 * 人期待的是"**这块被拿起来了**"，而不是"它被印了两遍"。
 * 套印错位那个装置仍然属于**纸**（牌面印刷层），不属于控件。
 *
 * ## 每一步都要有过渡
 *
 * 「你的 hover / active 都是没有过渡直接切换显示的」—— 所以这里不再"设个
 * 目标值就完事"，而是把两样东西都做成**补间的量**：
 *
 * | 量 | 从 | 到 |
 * | --- | --- | --- |
 * | `lift` | 0 | `stampLift(state)`（±几像素） |
 * | `mix` | 0（idle 的墨） | 1（hover 的墨） |
 *
 * 过渡本身走 `motion.ts`，规矩（90–220ms、急停、不过冲、位移小）来自
 * `visual-design.md` §6。**直接跳变是网页控件的观感**，这个项目不是。
 */

import { Container, Graphics, Rectangle } from 'pixi.js';
import type { FederatedPointerEvent } from 'pixi.js';
import { INK, stampInk, stampLift, type ControlStateName } from './ink';
import { currentFocusRing } from './focus';
import { touchHitSize } from './target';
import { uiLine, uiPad } from './style';
import { createAnimated, DURATION, drive, undrive, type Animated } from './motion';
import { createHoldClock, type HoldClock } from './hold';
import { currentTraits } from '../input';

/**
 * 控件可能的四种状态。
 *
 * ⚠️ 没有 `focus` / `selected` —— 那两个是**叠加**在状态上的标记
 * （注册标记、酸色外发光），不是互斥状态。混进这张表会让
 * "hover 时按下"这种组合没法表达。
 */
export type ControlState = ControlStateName;

/** 可聚焦控件的自增 id（同一个控件反复注册用同一个） */
let surfaceSeq = 0;

/** 一个状态下的补间量。绘制回调拿到的就是它 */
export interface ControlMotion {
  /** 已经补间过的位移（像素，负值向上） */
  readonly lift: number;
  /** 已经补间过的墨色进度（0 = idle 的墨，1 = hover 的墨） */
  readonly mix: number;
  /**
   * **长按确认的进度**（0..1）。没在长按的控件恒为 0。
   *
   * ⚠️ 它**不走补间**：那条从左到右的条必须真的反映"按了多久"，
   * 补间过的进度会骗人（松手之后还会往前涨一小截）。
   */
  readonly hold: number;
}

export interface SurfaceSpec {
  /** 控件自己的容器：hitArea 和事件都挂在它上面 */
  readonly container: Container;
  readonly width: number;
  readonly height: number;
  /** 状态一**变**就回调。**实现方唯一的绘制入口** */
  readonly onStateChange: (state: ControlState, motion: ControlMotion) => void;
  /** 每帧回调 —— 过渡进行中时才会调，用来重画那一帧 */
  readonly onFrame?: (state: ControlState, motion: ControlMotion) => void;
  /** 松开时（在块内）触发。按钮用它 */
  readonly onPress?: () => void;
  /**
   * **长按确认**：按住这么多毫秒之后触发 `onConfirm`（而不是松手时触发
   * `onPress`）。不给 = 普通按钮。
   *
   * ⚠️ 给了之后 `onPress` **一次都不会触发** —— 这一颗按钮的语义不再是
   * "按一下"，而是"按住确认"。两个都触发的话，一次长按会做两件事。
   */
  readonly holdMs?: number;
  /** 长按满了。见 `holdMs` */
  readonly onConfirm?: () => void;
  /**
   * **触控上「按住久一点 = 看这个控件的说明」。** 给毫秒数就启用。
   *
   * 触控没有 hover，功能性说明只能另找一路；按住是最不冲突的那个。
   * 「按住 = 确认」的按钮上按住仍以确认为准，但确认走完之前也会先把说明亮出来。
   */
  readonly hintHoldMs?: number;
  /** 按下时就触发。滑块用它开始拖拽 —— 拖拽不该等松开 */
  readonly onPressStart?: () => void;
  /** 可拖拽的控件（滑块）不要点击语义 */
  readonly pressable?: boolean;
  /**
   * **能不能用方向键/手柄走到它。** 默认 false —— 滑块/输入框的“左右”是调值/移光标，
   * 不是换焦点。按钮和开关显式打开。
   */
  readonly focusable?: boolean;
  readonly enabled?: boolean;
  readonly cursor?: 'pointer' | 'text' | 'default';
  /** 自定义命中区（滑块只让轨道那一条能点） */
  readonly hitArea?: Rectangle;
}

export interface Surface {
  readonly state: ControlState;
  readonly enabled: boolean;
  readonly motion: ControlMotion;
  /** 现在这一按已经按住够久、正在「看说明」吗（触控的 hover 替代） */
  readonly hinting: boolean;
  setEnabled(enabled: boolean): void;
  /**
   * **换触发方式**：给一个毫秒数就要求长按那么久（`onConfirm` 触发），
   * 给 `null` 就退回"松手即触发"（`onPress`）。
   *
   * 按下的过程中切换会**取消**当前这一次按压（`cancel`）—— 语义变了，
   * 已经按了一半的那一下不该算数。
   */
  setHoldMs(ms: number | null): void;
  /** 键盘/程序化地摆出 hover —— 也方便测试与冒烟脚本直接驱动 */
  setHovered(hovered: boolean): void;
  /** 摘掉所有监听器。⚠️ 容器销毁**不会**自动摘，控件必须调它 */
  destroy(): void;
}

/**
 * 装一台状态机到一个容器上。
 *
 * ⚠️ 用 `pointerdown` / `pointerup` 而不是 `pointertap`：只有按下/松开两个
 * 事件能表达"正在按"这个中间态，而"压印"就是那个中间态。
 * 顺带一个副作用是好的 —— 在按钮上按下、拖出去、再松开**不会**触发。
 */
export function createSurface(spec: SurfaceSpec): Surface {
  const { container } = spec;
  let hovered = false;
  let pressed = false;
  let enabled = spec.enabled ?? true;
  let state: ControlState = enabled ? 'idle' : 'disabled';

  /**
   * **焦点是叠加标记，不是控件状态**（见文件头）。
   *
   * 自己画四角套准、自己管显隐，所以每个控件**一行都不用改** ——
   * 加了走焦之后按钮/开关自动有焦点指示。
   */
  const focusMarks = new Graphics();
  focusMarks.visible = false;
  container.addChild(focusMarks);
  let focused = false;
  const paintFocus = (): void => {
    focusMarks.clear();
    focusMarks.visible = focused && enabled;
    if (focusMarks.visible) {
      drawRegistrationMarks(focusMarks, { width: spec.width, height: spec.height }, 1, INK.accent);
    }
  };

  const lift: Animated = createAnimated(0);
  const mix: Animated = createAnimated(0);
  /**
   * **现在要不要长按**，以及按多久。
   *
   * ⚠️ 它可以在运行中开关（`setHoldMs`）：同一个按钮在"平常"和"濒死"两种
   * 局面下是**两种触发方式**，而不是两个按钮 —— 濒死时的"放弃"要按住，
   * 平常的"结束回合"松手就该走。
   */
  let holdNeed: number | null = spec.holdMs ?? null;
  /** 长按计时器。第一次按下时才建（全项目就这一处） */
  let hold: HoldClock | null = null;
  /** 触控「按住看说明」的计时器（没给 `hintHoldMs` 就是 null）。见 `SurfaceSpec` */
  let hintClock: HoldClock | null =
    spec.hintHoldMs === undefined ? null : createHoldClock(spec.hintHoldMs);
  /** 这一按已经按住够久、正在「看说明」吗（`hintClock` 装满那一刻置位） */
  let hinting = false;
  /** 这一按是什么时候按下的（`performance.now()`，毫秒）。见 `hintThresholdReached` */
  let pressStartedAt = 0;
  /**
   * **按住时间到没到。**
   *
   * 除了每帧的 `hintClock`，还按真实时间再算一遍：切标签页 / 掉帧时 `stepMotion`
   * 可能根本没跑，只信帧时钟的话「按住看说明」会时灵时不灵。
   */
  const hintThresholdReached = (): boolean =>
    hintClock !== null && performance.now() - pressStartedAt >= (spec.hintHoldMs ?? 0);

  const motion = (): ControlMotion => ({
    lift: lift.value,
    mix: mix.value,
    hold: hold?.progress ?? 0,
  });

  /**
   * 正在走的那个过渡。
   *
   * ⚠️ 留着它只为 `destroy()` 里 `undrive()` —— 不摘的话，控件销毁之后
   * 每帧还会回调 `onFrame`，而那个回调是在往已销毁的容器上写位置
   * （滑块写 `name.y` / `readout.y`）。见 `motion.ts` 的 `drive()`。
   */
  let driver: { advance(deltaMS: number): boolean } | null = null;

  /**
   * 每帧驱动：**补间和长按共用一条**。
   *
   * ⚠️ 不能各挂一条：`drive()` 的队列里两条都活着的话，控件销毁时
   * 得记得摘两条；而长按还要在"补间早就停了、手指还按着"的时候继续跑
   * （那时候补间的两个量都已经到位，`advance` 都返回 false）。
   */
  const ensureDriver = (): void => {
    if (driver !== null) return;
    driver = {
      advance(deltaMS) {
        /**
         * ⚠️ **容器已经被销毁时，这一帧什么都别画。**
         *
         * 正常路径上 `destroy()` 会 `undrive()`（上面那个 `driver` 的说明）。
         * 但有些 owner 是**直接 `container.destroy({ children: true })`** 的
         * （比如那个确认弹窗一次销毁 box / overlay / button）—— 那条路上
         * 控件的 `onFrame` 会往已销毁的 `Graphics` 上 `clear()`，抛
         * "Cannot read properties of null (reading 'clear')"。
         * 在每帧入口看一眼 `destroyed` 是最省事也最不会漏的兜底。
         */
        if (container.destroyed) {
          driver = null;
          return false;
        }
        const a = lift.advance(deltaMS);
        const b = mix.advance(deltaMS);
        const stepped = hold?.advance(deltaMS) ?? false;
        if (stepped) spec.onConfirm?.();
        // 「按住看说明」：装满那一刻置位，此后不再需要每帧推
        if (hintClock?.advance(deltaMS) === true) hinting = true;
        spec.onFrame?.(state, motion());
        // 长按（确认或看说明）没结束之前必须一直跑
        const busy = a || b || (hold?.active ?? false) || (hintClock?.active ?? false);
        if (!busy) driver = null;
        return busy;
      },
    };
    drive(driver);
  };

  /** 把两个补间量的目标对齐当前状态，并登记进每帧队列 */
  const retarget = (immediate: boolean): void => {
    const liftTarget = enabled ? stampLift(state) : 0;
    const mixTarget = state === 'hover' && enabled ? 1 : 0;
    if (immediate) {
      lift.snap(liftTarget);
      mix.snap(mixTarget);
      return;
    }
    lift.to(liftTarget, DURATION.fast);
    // 墨比位移稍慢一点：位移是"被拿起来"，颜色是"多印了一遍"，后者该稍后到
    mix.to(mixTarget, DURATION.normal);
    ensureDriver();
  };

  const resolve = (): ControlState => {
    if (!enabled) return 'disabled';
    if (pressed) return 'press';
    if (hovered) return 'hover';
    return 'idle';
  };

  const sync = (immediate = false): void => {
    const next = resolve();
    if (next === state && !immediate) return;
    state = next;
    retarget(immediate);
    spec.onStateChange(state, motion());
  };

  const setCursor = (): void => {
    container.cursor = enabled ? (spec.cursor ?? 'pointer') : 'default';
  };

  /**
   * hover 只在**能 hover 的指针**上发生。
   *
   * ⚠️ 手指没有 hover 这个中间态；判断来自 `PointerEvent` 的属性
   *    （pointerType / 接触面），**不是**“触屏模式”。
   */
  const onOver = (): void => {
    if (!currentTraits().canHover) return;
    hovered = true;
    sync();
  };
  /**
   * 指针已经在控件上、但设备从触控切回鼠标时，不会再触发 `pointerover` —— 这里补上。
   * 同理，从鼠标切到触控时 `pointermove` 会因 `canHover=false` 而不生效。
   */
  const onMove = (): void => {
    if (hovered || !currentTraits().canHover) return;
    hovered = true;
    sync();
  };
  const onOut = (): void => {
    hovered = false;
    pressed = false;
    // 指针离开（或在外松开）= 这次长按不算，条子立刻归零
    hold?.cancel();
    hintClock?.cancel();
    hinting = false;
    sync();
    if (hold !== null) spec.onFrame?.(state, motion());
  };
  const onDown = (event: FederatedPointerEvent): void => {
    if (!enabled) return;
    // 只认主键：右键/中键不该把按钮按下去
    if (event.button !== 0) return;
    pressed = true;
    pressStartedAt = performance.now();
    // 手指按下不该留下 hover 抬起态
    if (!currentTraits().canHover) hovered = false;
    if (holdNeed !== null) {
      hold ??= createHoldClock(holdNeed);
      hold.begin();
    }
    // 按住久一点 = 看说明（触控的 hover 替代）。确认态下也开 ——
    // 确认要 3 秒，前面这段时间足够先把说明亮出来。
    hintClock?.begin();
    sync();
    // 长按要开始涨了 —— 状态可能没变（idle→press 变过一次），但每帧驱动
    // 必须起来。`sync()` 只在状态**变了**的时候才 `retarget`，所以这里补一次
    ensureDriver();
    spec.onPressStart?.();
  };
  const onUp = (): void => {
    if (!pressed) return;
    // 按住看过说明的这一下**不算点按**：否则长按会顺手把动作做掉。
    // ⚠️ 同时看帧时钟和真实时间（见 `hintThresholdReached`）。
    const wasHinting = hinting || (hold === null && hintThresholdReached());
    pressed = false;
    if (!currentTraits().canHover) hovered = false;
    hold?.cancel();
    hinting = false;
    hintClock?.cancel();
    sync();
    if (!enabled) return;
    // ⚠️ 长按确认的按钮**不走** `onPress`：它的确认在按住那一刻已经发生过了
    if (hold === null) {
      if (!wasHinting) spec.onPress?.();
    } else {
      spec.onFrame?.(state, motion());
    }
  };

  const pressable = spec.pressable ?? true;
  let unregisterFocus: (() => void) | null = null;
  if (spec.focusable === true && pressable) {
    const ring = currentFocusRing();
    if (ring !== null) {
      unregisterFocus = ring.register({
        id: 'surface#' + String(surfaceSeq++),
        bounds: () => container.getBounds(),
        activate: () => {
          if (!enabled) return;
          // 长按确认的按钮：手柄 A 直接确认（手柄没有“一直按住”这个自然语义）
          if (holdNeed !== null) spec.onConfirm?.();
          else spec.onPress?.();
        },
        setFocused: (on) => {
          focused = on;
          paintFocus();
        },
      });
    }
  }
  container.eventMode = 'static';
  if (spec.hitArea !== undefined) {
    container.hitArea = spec.hitArea;
  } else {
    // 触控：视觉不动，命中区往外扩到最小可点尺寸（鼠标下恒等）
    const hit = touchHitSize(spec.width, spec.height);
    container.hitArea = new Rectangle(
      (spec.width - hit.width) / 2,
      (spec.height - hit.height) / 2,
      hit.width,
      hit.height,
    );
  }
  setCursor();
  if (pressable) {
    container.on('pointerover', onOver);
    container.on('pointermove', onMove);
    container.on('pointerout', onOut);
    container.on('pointerdown', onDown);
    container.on('pointerup', onUp);
    container.on('pointerupoutside', onOut);
    // 浏览器可能抢走触摸（滚动/缩放/系统手势）—— 取消按下的那一下
    container.on('pointercancel', onOut);
  }
  // 首帧直接落位：一进场所有控件一起滑一遍是噪音，不是动效
  retarget(true);

  return {
    get state() {
      return state;
    },
    get enabled() {
      return enabled;
    },
    get motion() {
      return motion();
    },
    get hinting() {
      // 帧时钟没跑（掉帧 / 后台）时，按真实时间补上 —— 见 `hintThresholdReached`
      return hinting || (pressed && hintThresholdReached());
    },
    setEnabled(next: boolean) {
      if (enabled === next) return;
      enabled = next;
      if (!next) {
        pressed = false;
        hovered = false;
        hold?.cancel();
        hintClock?.cancel();
        hinting = false;
      }
      container.eventMode = next ? 'static' : 'none';
      setCursor();
      paintFocus();
      // 禁用/解禁**不补间**：撤墨是一个动作，慢慢淡出像 App
      state = resolve();
      retarget(true);
      spec.onStateChange(state, motion());
    },
    setHoldMs(ms: number | null) {
      holdNeed = ms;
      hold?.cancel();
      if (holdNeed === null) hold = null;
      // 切成「按住 = 确认」之后，按住不再兼作看说明
      hintClock?.cancel();
      hinting = false;
      spec.onFrame?.(state, motion());
    },
    setHovered(next: boolean) {
      if (hovered === next) return;
      hovered = next;
      sync();
    },
    destroy() {
      unregisterFocus?.();
      unregisterFocus = null;
      // ⚠️ 先把每帧回调摘掉，再摘监听器 —— 见 `driver` 的说明
      if (driver !== null) {
        undrive(driver);
        driver = null;
      }
      hold?.cancel();
      hold = null;
      hintClock?.cancel();
      hintClock = null;
      hinting = false;
      container.off('pointerover', onOver);
      container.off('pointermove', onMove);
      container.off('pointerout', onOut);
      container.off('pointerdown', onDown);
      container.off('pointerup', onUp);
      container.off('pointerupoutside', onOut);
      container.off('pointercancel', onOut);
    },
  };
}

export { stampInk };

// ============================================================
// 画一块印章
// ============================================================

export interface StampBox {
  readonly x?: number;
  readonly y?: number;
  readonly width: number;
  readonly height: number;
}

/**
 * 把一个矩形印章画进 `Graphics`。
 *
 * `lift` 是已经补间过的位移（`motion.ts` 给的，负值向上）。
 *
 * ⚠️ **它只画块，不画字。** 文字的位置和颜色由调用方自己按状态设 ——
 * 因为"抬起的是块，还是连字一起抬"必须由代码结构明确，不能靠自觉。
 * 现在两者**一起抬**（整块东西被拿起来了），所以调用方把 `lift` 也加到文字上。
 */
export function drawStamp(
  graphics: Graphics,
  ink: { fill: number; edge: number; label: number },
  box: StampBox,
  scale: number,
  lift = 0,
): void {
  const edge = uiLine(2, scale);
  graphics
    .rect(box.x ?? 0, (box.y ?? 0) + lift, box.width, box.height)
    .fill({ color: ink.fill })
    .stroke({ color: ink.edge, width: edge, alignment: 0 });
  void stampInk;
}

/**
 * **注销杠** —— 禁用状态的第二个装置（`docs/ui-design.md` §2.3）。
 *
 * 一道 15° 的斜切横贯整块。用斜线而不是"黑条盖住文字"：黑底上盖黑条
 * 是看不见的，用户只会觉得标签坏了。斜杠是公文里"作废"的真实惯例，
 * 也和 `visual-design.md` §8 的「弃牌堆 = 被划掉的一段」是同一个装置。
 */
export function drawRedactionSlash(
  graphics: Graphics,
  box: StampBox,
  scale: number,
  color: number,
  lift = 0,
): void {
  const x = box.x ?? 0;
  const y = (box.y ?? 0) + lift;
  const thickness = Math.max(2, uiLine(1, scale) * 2);
  // 15°：斜切但不夸张。斜率固定的好处是不同尺寸的控件上"作废感"一致
  const rise = Math.round(box.height * 0.27);
  const drop = box.height * 0.5 - rise * 0.5;
  graphics
    .moveTo(x - thickness, y + drop + rise + thickness)
    .lineTo(x + box.width + thickness, y + drop - thickness)
    .stroke({ color, width: thickness, cap: 'butt' });
}

/**
 * **长按确认那条从左到右的条。**
 *
 * 评审：「长按期间有一个按钮上要有一个从左到右的条。」
 *
 * 画法（`visual-design.md` 的印刷语言）：
 *
 * - 它是一条**实心直角**，高度撑满印章内部（不是一根 2px 的进度条 ——
 *   那读起来是网页的下载进度）
 * - 从**左边缘**长到右边缘，和"印版从左往右压过去"同一个方向
 * - 位置在**字的下面**：调用方先画块和条、再画字，所以字始终压在上面，
 *   条子扫过去不会把标签吃掉
 *
 * `progress` 是 0..1 的**真实**进度（不补间，见 `ControlMotion.hold`）。
 *
 * ⚠️ 进度太小时**不画**：`w < 1` 的矩形在有些缩放下会渲染成一条半透明的
 * 竖线，看着像"这里有一条坏线"，而不是"条子刚开始涨"。
 */
export function drawHoldBar(
  graphics: Graphics,
  box: StampBox,
  scale: number,
  progress: number,
  color: number,
  lift = 0,
): void {
  const p = Math.max(0, Math.min(1, progress));
  if (p <= 0) return;
  const edge = uiLine(2, scale);
  const x = (box.x ?? 0) + edge;
  const y = (box.y ?? 0) + lift + edge;
  const w = (box.width - edge * 2) * p;
  const h = box.height - edge * 2;
  if (w < 1 || h <= 0) return;
  graphics.rect(x, y, w, h).fill({ color });
}

/**
 * 四角注册标记 —— 焦点用的装置（`docs/ui-design.md` §5）。
 *
 * 用套准标记而不是"一圈虚线边框"：虚线框是屏幕语言里最常见的 focus ring，
 * 而这个项目里最像"这里被套准了"的东西是印刷机四角的那四个 ⌐。
 */
export function drawRegistrationMarks(
  graphics: Graphics,
  box: StampBox,
  scale: number,
  color: number,
  lift = 0,
): void {
  const inset = uiPad(-4, scale);
  const arm = uiPad(8, scale);
  const x = (box.x ?? 0) + inset;
  const y = (box.y ?? 0) + lift + inset;
  const w = box.width - inset * 2;
  const h = box.height - inset * 2;
  const line = uiLine(1, scale);
  const corner = (cx: number, cy: number, dx: number, dy: number): void => {
    graphics.rect(cx, cy, arm * dx, line).fill({ color });
    graphics.rect(cx, cy, line, arm * dy).fill({ color });
  };
  corner(x, y, 1, 1);
  corner(x + w, y, -1, 1);
  corner(x, y + h, 1, -1);
  corner(x + w, y + h, -1, -1);
}
